import { useEffect, useId, useState } from "react";
import { ColorPicker } from "primereact/colorpicker";
import { classNames } from "primereact/utils";
import styles from "./DesignStudioAdvancedEditor.module.scss";
import { isValidHexColor } from "../../../../../../common/infrastructure/utilities/colorUtility";

interface IDesignStudioColorFieldProps {
  label: string;
  /** What screen readers call the colour, e.g. "Бутон - цвят на фон". */
  name: string;
  /** Six hex digits, no '#'. */
  value: string;
  /** Shown greyed with a "Вграден" tag: the colour is the built-in one, not the studio's. */
  inherited?: boolean;
  disabled?: boolean;
  onChange: (hex: string) => void;
}

const bare = (hex: string) => hex.trim().replace(/#/g, "").toLowerCase();

export const DesignStudioColorField = ({
  label,
  name,
  value,
  inherited = false,
  disabled = false,
  onChange,
}: IDesignStudioColorFieldProps) => {
  const id = useId();
  const errorId = `${id}-error`;
  const [draft, setDraft] = useState(value);

  // Follow the colour when it changes elsewhere (the picker, auto text, a quick action),
  // but leave a half-typed value alone while it still says the same colour.
  useEffect(() => {
    setDraft((current) => (bare(current) === bare(value) ? current : value));
  }, [value]);

  const isInvalid = !isValidHexColor(draft);

  const onDraftChange = (next: string) => {
    const cleaned = next.replace(/#/g, "").trim().slice(0, 6);
    setDraft(cleaned);

    if (isValidHexColor(cleaned)) {
      onChange(cleaned);
    }
  };

  return (
    <div
      className={classNames(styles.field, {
        [styles.field_inherited]: inherited,
        [styles.field_disabled]: disabled,
      })}
    >
      <label className={styles.field_label} htmlFor={id}>
        {label}
        {inherited && <span className={styles.field_tag}>Вграден</span>}
      </label>

      <div className={styles.field_controls}>
        <ColorPicker
          inputId={`${id}-swatch`}
          aria-label={name}
          value={value}
          format="hex"
          disabled={disabled}
          appendTo={document.body}
          className={styles.swatch}
          panelClassName={styles.panel}
          onChange={(event) => {
            if (typeof event.value === "string") {
              onChange(event.value);
            }
          }}
        />

        <span
          className={classNames(styles.hex, { [styles.hex_invalid]: isInvalid })}
        >
          <span className={styles.hex_hash} aria-hidden>
            #
          </span>
          <input
            id={id}
            className={styles.hex_input}
            aria-label={`${name} (HEX)`}
            aria-invalid={isInvalid}
            aria-describedby={isInvalid ? errorId : undefined}
            value={draft}
            maxLength={7}
            spellCheck={false}
            autoComplete="off"
            disabled={disabled}
            onChange={(event) => onDraftChange(event.target.value)}
          />
        </span>
      </div>

      {isInvalid && (
        <span id={errorId} className={styles.field_error} role="alert">
          Невалиден цвят
        </span>
      )}
    </div>
  );
};
